import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from '../../translations';
import FormField from './FormField';
import FormActions from './FormActions';
import ErrorMessage from './ErrorMessage';
import IconSelector from './IconSelector';
import BackgroundImageSelector from './BackgroundImageSelector';
import { FolderIcon, CodeBracketIcon, BuildingOfficeIcon, UserGroupIcon } from '@heroicons/react/24/outline';

const ProjectForm = ({ onSubmit, onCancel, isLoading, error, user }) => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  
  const [formData, setFormData] = useState({
    projectName: '',
    projectCode: '',
    hotelName: user?.hotel_name || '',
    departmentName: user?.department_name || '',
    projectIcon: 'icons8-maintenance-50', // Varsayılan icon
    backgroundImage: 'default-bg' // Varsayılan background
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.projectName.trim()) {
      newErrors.projectName = language === 'tr' ? 'Proje adı gereklidir' : 'Project name is required';
    }
    
    if (!formData.projectCode.trim()) {
      newErrors.projectCode = language === 'tr' ? 'Proje kodu gereklidir' : 'Project code is required';
    } else if (!/^[A-Z0-9]{2,10}$/.test(formData.projectCode)) {
      newErrors.projectCode = language === 'tr' 
        ? 'Proje kodu 2-10 karakter arası büyük harf ve rakam olmalıdır'
        : 'Project code must be 2-10 characters of uppercase letters and numbers';
    }
    
    if (!formData.hotelName.trim()) {
      newErrors.hotelName = language === 'tr' ? 'Otel adı gereklidir' : 'Hotel name is required';
    }
    
    if (!formData.departmentName.trim()) {
      newErrors.departmentName = language === 'tr' ? 'Departman adı gereklidir' : 'Department name is required';
    }
    
    if (!formData.projectIcon) {
      newErrors.projectIcon = language === 'tr' ? 'Proje ikonu seçilmelidir' : 'Project icon must be selected';
    }
    
    if (!formData.backgroundImage) {
      newErrors.backgroundImage = language === 'tr' ? 'Arkaplan resmi seçilmelidir' : 'Background image must be selected';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && <ErrorMessage message={error} />}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          label={language === 'tr' ? 'Proje Adı' : 'Project Name'}
          icon={FolderIcon}
          value={formData.projectName}
          onChange={(value) => handleChange('projectName', value)}
          placeholder={language === 'tr' ? 'Proje adını girin' : 'Enter project name'}
          error={errors.projectName}
          required
        />
        
        <FormField
          label={language === 'tr' ? 'Proje Kodu' : 'Project Code'}
          icon={CodeBracketIcon}
          value={formData.projectCode}
          onChange={(value) => handleChange('projectCode', value.toUpperCase())}
          placeholder={language === 'tr' ? 'PROJ01' : 'PROJ01'}
          error={errors.projectCode}
          maxLength={10}
          required
        />
        
        <FormField
          label={language === 'tr' ? 'Otel Adı' : 'Hotel Name'}
          icon={BuildingOfficeIcon}
          value={formData.hotelName}
          onChange={(value) => handleChange('hotelName', value)}
          placeholder={language === 'tr' ? 'Otel adını girin' : 'Enter hotel name'}
          error={errors.hotelName}
          required
        />
        
        <FormField
          label={language === 'tr' ? 'Departman Adı' : 'Department Name'}
          icon={UserGroupIcon}
          value={formData.departmentName}
          onChange={(value) => handleChange('departmentName', value)}
          placeholder={language === 'tr' ? 'Departman adını girin' : 'Enter department name'}
          error={errors.departmentName}
          required
        />
      </div>
      
      {/* Icon Selection */}
      <div className="col-span-full">
        <IconSelector
          selectedIcon={formData.projectIcon}
          onIconSelect={(iconId) => handleChange('projectIcon', iconId)}
          error={errors.projectIcon}
        />
      </div>
      
      {/* Background Image Selection */}
      <div className="col-span-full">
        <BackgroundImageSelector
          selectedBackground={formData.backgroundImage}
          onBackgroundSelect={(backgroundId) => handleChange('backgroundImage', backgroundId)}
          error={errors.backgroundImage}
        />
      </div>
      
      <FormActions 
        onCancel={onCancel}
        isLoading={isLoading}
        language={language}
      />
    </form>
  );
};

export default ProjectForm;